import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useController, useFormContext } from 'react-hook-form';
import DatePicker, { registerLocale } from 'react-datepicker';
import { fr } from 'date-fns/locale/fr';
import { format, parseISO } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';

import 'react-datepicker/dist/react-datepicker.css';
import './InputDate.scss';

registerLocale('fr', fr);

const CalendarPortal = ({ children }) => createPortal(children, document.body);

// One date field of the range. Stores an ISO "yyyy-MM-dd" string in the form (unlike InputDate,
// which stores a raw Date object) so TableFilters' generic {min,max} flattening produces a
// plain string for createdAtMin/createdAtMax — exactly what the backend's LocalDate.parse
// expects, same convention InputTextRange already uses for its numeric bounds.
const RangeDateField = ({ name, placeholder, validationRules, disabled }) => {
    const { control } = useFormContext();
    const {
        field: { onChange, onBlur, value, ref },
    } = useController({ name, control, rules: validationRules, defaultValue: '' });
    const datePickerRef = useRef(null);
    const wrapperRef = useRef(null);

    // Same outside-click handling as InputDate — the calendar is portalled to <body>, and a
    // modal's own mousedown-stopPropagation would otherwise block react-datepicker's default
    // close-on-outside-click listener.
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                wrapperRef.current && !wrapperRef.current.contains(e.target)
                && !e.target.closest?.('.react-datepicker-popper')
            ) {
                datePickerRef.current?.setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside, true);
        return () => document.removeEventListener('mousedown', handleClickOutside, true);
    }, []);

    return (
        <div className="input-date-wrapper" ref={wrapperRef} style={{ flex: 1, minWidth: 0 }}>
            <DatePicker
                ref={datePickerRef}
                selected={value ? parseISO(value) : null}
                onChange={(date) => onChange(date ? format(date, 'yyyy-MM-dd') : '')}
                onBlur={onBlur}
                placeholderText={placeholder}
                wrapperClassName="datepicker-wrapper"
                popperContainer={CalendarPortal}
                locale="fr"
                dateFormat="yyyy-MM-dd"
                disabled={disabled}
                customInput={<input ref={ref} id={name} className="form-control" />}
            />
            <FontAwesomeIcon
                icon={faCalendarAlt}
                className="input-date-icon"
                onClick={() => !disabled && datePickerRef.current?.setOpen(true)}
            />
        </div>
    );
};

const InputDateRange = ({
    name,
    validationRules = {},
    className = '',
    disabled = false,
    style,
}) => {
    const { watch } = useFormContext();
    const minValue = watch ? watch(`${name}.min`) : null;

    return (
        <div className={`d-flex gap-2 ${className}`} style={style}>
            <RangeDateField
                name={`${name}.min`}
                placeholder="Du"
                validationRules={validationRules}
                disabled={disabled}
            />
            <RangeDateField
                name={`${name}.max`}
                placeholder="Au"
                disabled={disabled}
                validationRules={{
                    ...validationRules,
                    validate: (value) => {
                        if (!value || !minValue) return true;
                        return value >= minValue || 'La date de fin doit être postérieure ou égale à la date de début';
                    },
                }}
            />
        </div>
    );
};

InputDateRange.isRangeFilter = true;

export default InputDateRange;
